/**
 * Server functions for coach onboarding status, referrals, and related queries.
 * Aliases: onboarding, referral, finish-onboarding
 */
import { createServerFn } from "@tanstack/react-start";
import { nanoid } from "nanoid";

import type {
  CoachDetailsSelect,
  CoachExpertiseSelect,
  CoachSelect,
  OfferSelect,
  ProfileSelect,
} from "~/../drizzle/schema";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import type { ReferralCreditSummary } from "~/lib/server/referral-rewards";

// Full coach type for onboarding
type CoachFull = CoachSelect & {
  profile: ProfileSelect | null;
  details: CoachDetailsSelect | null;
  mainOffer: OfferSelect | null;
  expertises: CoachExpertiseSelect[];
};

export interface DashboardOnboarding {
  referralCode: string | null;
  /** Referrer's referral credit, derived from the `coachReferralRewards` ledger (R17). */
  creditInfo: ReferralCreditSummary;
  coach: CoachFull;
  profile_manually_edited: boolean;
  onboarding_completed_at: Date | null;
}

export const getOnboardingStatus = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<DashboardOnboarding> => {
    const { getDBWithInit } = await import("~/lib/db");

    const coachShared = context.coach;
    if (!coachShared) {
      throw new Error("Coach not found");
    }

    const db = await getDBWithInit();

    const coach = await db.query.coaches.findFirst({
      where: { id: coachShared.id },
      with: { profile: true, details: true, mainOffer: true, expertises: true },
    });
    if (!coach) {
      throw new Error("Coach not found");
    }

    const { getReferralCreditSummary } =
      await import("~/lib/server/referral-rewards");
    const creditInfo = await getReferralCreditSummary(db, coachShared.id);

    return {
      referralCode: coach.referralCode,
      creditInfo,
      coach,
      profile_manually_edited: coach.profile_manually_edited,
      onboarding_completed_at: coach.onboarding_completed_at,
    };
  });

export const generateReferralCode = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<string> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { eq } = await import("drizzle-orm");

    const coach = context.coach;
    if (!coach) {
      throw new Error("Coach not found");
    }

    const db = await getDBWithInit();
    const { coaches } = await import("~/../drizzle/schema");

    let referralCode = nanoid(8);
    while (
      await db.query.coaches.findFirst({
        where: { referralCode },
        columns: { id: true },
      })
    ) {
      referralCode = nanoid(8);
    }

    await db
      .update(coaches)
      .set({ referralCode })
      .where(eq(coaches.id, coach.id));

    return referralCode;
  });
